import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Window } from '../Window/Window';
import { glitchAnimation, cmdChaosAnimation } from '../../styles/meltEffects';

// Define these components first
const CommandLine = styled.div`
  display: flex;
  gap: 8px;
`;

const Input = styled.input`
  background: transparent;
  color: #00ff00;
  font-family: 'VT323', monospace;
  border: none;
  outline: none;
  width: calc(100% - 100px);
  caret-color: #00ff00;
`;

const Prompt = styled.span`
  color: #00ff00;
`;

// Now we can use them in Terminal
const Terminal = styled.div`
  background: black;
  color: #00ff00;
  font-family: 'VT323', monospace;
  padding: 10px;
  height: calc(100% - 25px);
  overflow-y: auto;
  white-space: pre-wrap;
  
  &.melting {
    animation: ${glitchAnimation} 0.1s steps(2) infinite;
    text-shadow: 2px 0 0 rgba(255,0,0,0.5), -2px 0 0 rgba(0,255,0,0.5);
    
    ${CommandLine} {
      animation: ${cmdChaosAnimation} 0.15s steps(2) infinite;
      transform-origin: left center;
    }

    ${Input} {
      animation: ${glitchAnimation} 0.2s steps(2) infinite;
      filter: hue-rotate(${props => Math.random() * 360}deg);
    }

    ${Prompt} {
      animation: ${cmdChaosAnimation} 0.1s steps(2) infinite;
      filter: brightness(2);
    }
  }
`;

// Add this new component for the disco effect
const DiscoOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  pointer-events: none;
  animation: disco 0.5s infinite;
  
  @keyframes disco {
    0% { background: rgba(255, 0, 0, 0.2); }
    33% { background: rgba(0, 255, 0, 0.2); }
    66% { background: rgba(0, 0, 255, 0.2); }
    100% { background: rgba(255, 0, 0, 0.2); }
  }
`;

const DiscoBall = styled.div`
  position: fixed;
  top: -100px;
  left: 50%;
  transform: translateX(-50%);
  width: 200px;
  height: 200px;
  background: radial-gradient(circle at 50% 50%, #fff, #888);
  border-radius: 50%;
  box-shadow: 0 0 100px #fff;
  animation: spin 3s linear infinite;
  z-index: 10000;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: repeating-linear-gradient(
      0deg,
      transparent,
      rgba(255, 255, 255, 0.2) 4px,
      transparent 8px
    );
    border-radius: 50%;
  }
  
  @keyframes spin {
    from { transform: translateX(-50%) rotate(0deg); }
    to { transform: translateX(-50%) rotate(360deg); }
  }
`;

const Confetti = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`;

export const CMD = ({ 
  onClose, 
  isActive, 
  onActivate, 
  isMinimized, 
  isMaximized, 
  onMinimize, 
  onMaximize, 
  onOpenAbout,
  initialPosition,
  onOpenAppleFarm,
  onSystemCrash,
  isMelting = false,
  mp3Ref
}) => {
  const [input, setInput] = useState('');
  const [history, setHistory] = useState(['Welcome to DavidOS Command Prompt', 'Type "help" for available commands', '']);
  const [isDisco, setIsDisco] = useState(false);
  const [awaitingDiscoConfirmation, setAwaitingDiscoConfirmation] = useState(false);
  const terminalRef = useRef(null);
  const confettiRef = useRef(null);
  const [currentDirectory, setCurrentDirectory] = useState('C:\\');
  const [isMining, setIsMining] = useState(false);
  const miningInterval = useRef(null);

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [history]);

  const createConfetti = () => {
    const colors = ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff'];
    for (let i = 0; i < 100; i++) {
      const confetti = document.createElement('div');
      confetti.style.position = 'absolute';
      confetti.style.left = Math.random() * 100 + 'vw';
      confetti.style.top = -10 + 'px';
      confetti.style.width = '10px';
      confetti.style.height = '10px';
      confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
      confetti.style.transform = `rotate(${Math.random() * 360}deg)`;
      confetti.style.animation = `fall ${Math.random() * 3 + 2}s linear`;
      confettiRef.current?.appendChild(confetti);
    }
  };

  const startDisco = () => {
    setHistory(prev => [...prev, 'Starting disco mode...', '']);
    setIsDisco(true);
    createConfetti();
    setTimeout(() => {
      setIsDisco(false);
      const confettiElements = confettiRef.current?.children || [];
      for (let i = confettiElements.length - 1; i >= 0; i--) {
        confettiElements[i].remove();
      }
    }, 5000);
  };

  const startMiningAnimation = () => {
    let frame = 0;
    const frames = [
      `
      ₿₿₿₿₿₿₿₿₿₿₿₿₿₿
      ₿ MINING ₿   [□□□□□□□□□□] 0%
      ₿₿₿₿₿₿₿₿₿₿₿₿₿₿
      
            ⚡ ⚡ ⚡
           /|\\
          / | \\
         /  |  \\
        /   |   \\
       /    |    \\
      /_____⚡_____\\
      [drilling...]
      `,
      `
      ₿₿₿₿₿₿₿₿₿₿₿₿₿₿
      ₿ MINING ₿   [■□□□□□□□□□] 10%
      ₿₿₿₿₿₿₿₿₿₿₿₿₿₿
      
            ⚡ ⚡ ⚡
           \\|/
          \\ | /
         \\  |  /
        \\   |   /
       \\    |    /
      \\_____⚡_____/
      [drilling...]
      `,
      `
      ₿₿₿₿₿₿₿₿₿₿₿₿₿₿
      ₿ MINING ₿   [■■□□□□□□□□] 20%
      ₿₿₿₿₿₿₿₿₿₿₿₿₿₿
      
            ⚡ ⚡ ⚡
           /|\\
          / | \\
         /  |  \\
        /   |   \\
       /    |    \\
      /_____⚡_____\\
      [drilling...]
      `
    ];

    setIsMining(true);
    setHistory(prev => [...prev, 
      `> important_system_file.dll`,
      `WARNING: you opened a based dll file!`,
      `Initializing mining sequence...`,
      frames[0],
      ''
    ]);

    let progress = 0;
    miningInterval.current = setInterval(() => {
      frame = (frame + 1) % frames.length;
      progress += 10;
      
      if (progress >= 100) {
        clearInterval(miningInterval.current);
        setIsMining(false);
        setHistory(prev => [...prev,
          `mining complete! 0.00000001 BTC earned`,
          `* SBF would be proud, go agane? *`,
          ''
        ]);
        return;
      }

      setHistory(prev => {
        const newHistory = [...prev];
        newHistory[newHistory.length - 2] = frames[frame].replace('MINING', `MINING ${progress}%`);
        return newHistory;
      });
    }, 500);
  };

  const handleCommand = (cmd) => {
    const command = cmd.toLowerCase().trim();

    if (command === 'wtf') {
      if (mp3Ref && mp3Ref.current) {
        mp3Ref.current.cleanup();
      }

      const frames = [
        '[ ⠋ ] am i being mogged?...',
        '[ ⠙ ] am i being mogged?...',
        '[ ⠹ ] am i being mogged?...',
        '[ ⠸ ] am i being mogged?...',
        '[ ⠼ ] am i being mogged?...',
        '[ ⠴ ] am i being mogged?...',
        '[ ⠦ ] am i being mogged?...',
        '[ ⠧ ] am i being mogged?...',
        '[ ⠇ ] am i being mogged?...',
        '[ ⠏ ] am i being mogged?...'
      ];

      setHistory(prev => [...prev, `> ${command}`, 'ehmmmmm????...', '']);
      
      let frameIndex = 0;
      const animationInterval = setInterval(() => {
        setHistory(prev => [...prev.slice(0, -1), frames[frameIndex]]);
        frameIndex = (frameIndex + 1) % frames.length;
      }, 100); // Update every 100ms

      setTimeout(() => {
        clearInterval(animationInterval);
        onSystemCrash();
      }, 1500);
      
      return;
    }

    // Special handling for stop command during mining
    if (isMining && command === 'stop') {
      clearInterval(miningInterval.current);
      setIsMining(false);
      setHistory(prev => [...prev, 
        `> stop`,
        `damn it, mining operation interrupted!`,
        `* SBF would be proud *`,
        ''
      ]);
      return;
    }

    // Don't allow other commands while mining animation is playing
    if (isMining) {
      setHistory(prev => [...prev, 
        `> ${command}`, 
        'Cannot execute commands during mining operation...',
        'Type "stop" to interrupt the mining process',
        ''
      ]);
      return;
    }

    // Handle disco confirmation
    if (awaitingDiscoConfirmation) {
      if (command === 'y') {
        setAwaitingDiscoConfirmation(false);
        startDisco();
        return;
      } else if (command === 'n') {
        setAwaitingDiscoConfirmation(false);
        setHistory(prev => [...prev, `> ${command}`, 'Disco mode cancelled.', '']);
        return;
      } else {
        setHistory(prev => [...prev, `> ${command}`, 'Please enter (y) or (n)', '']);
        return;
      }
    }

    // Regular command handling
    let response = '';

    switch (command) {
      case 'help':
        response = `Available commands:
- help: Show this help message
- clear: Clear the terminal
- cd [folder]: Change directory
- dir: List directory contents
- date: Show current date
- time: Show current time
- type [file]: Display file contents`;
        break;

      case 'clear':
        setHistory(['']);
        return;

      case 'dir':
        if (currentDirectory === 'C:\\') {
          response = `
Directory of C:\\
12/25/2023  12:00 PM    <DIR>    davidos
12/25/2023  12:00 PM    <DIR>    Users
12/25/2023  12:00 PM    <DIR>    totallynormalfolder
12/25/2023  12:00 PM    <FILE>   readme.txt
12/25/2023  12:00 PM    <FILE>   important_system_file.dll
12/25/2023  12:00 PM    <FILE>   snake.txt`;
        } else if (currentDirectory === 'C:\\Users') {
          response = `
Directory of C:\\Users
12/25/2023  12:00 PM    <DIR>    Guest`;
        } else if (currentDirectory === 'C:\\Users\\Guest') {
          response = `
Directory of C:\\Users\\Guest
12/25/2023  12:00 PM    <DIR>    Documents`;
        } else if (currentDirectory === 'C:\\Users\\Guest\\Documents') {
          response = `
Directory of C:\\Users\\Guest\\Documents
12/25/2023  12:00 PM    <FILE>   davidos.txt
12/25/2023  12:00 PM    <FILE>   apple farm.txt`;
        } else if (currentDirectory === 'C:\\davidos') {
          response = `
Directory of C:\\davidos
12/25/2023  12:00 PM    <FILE>   system32.dll`;
        } else if (currentDirectory === 'C:\\totallynormalfolder') {
          response = `
Directory of C:\\totallynormalfolder
12/25/2023  12:00 PM    <FILE>   disco.exe`;
        }
        break;

      case 'cd davidos':
        if (currentDirectory === 'C:\\') {
          setCurrentDirectory('C:\\davidos');
          response = `tf are u doing in here, get out`;
        } else {
          response = 'Invalid directory';
        }
        break;

      case 'cd documents':
        if (currentDirectory === 'C:\\Users\\Guest') {
          setCurrentDirectory('C:\\Users\\Guest\\Documents');
          response = 'Directory changed to C:\\Users\\Guest\\Documents';
        } else {
          response = 'Invalid directory';
        }
        break;

      case 'cd windows':
      case 'cd system32':
      case 'cd program files':
      case 'cd program files (x86)':
        response = `Access denied: Administrator permissions required`;
        break;

      case 'cd users':
        if (currentDirectory === 'C:\\') {
          setCurrentDirectory('C:\\Users');
          response = 'Directory changed to C:\\Users';
        } else {
          response = 'Invalid directory';
        }
        break;

      case 'cd guest':
        if (currentDirectory === 'C:\\Users') {
          setCurrentDirectory('C:\\Users\\Guest');
          response = 'Directory changed to C:\\Users\\Guest';
        } else {
          response = 'Invalid directory';
        }
        break;

      case 'cd totallynormalfolder':
        if (currentDirectory === 'C:\\') {
          setCurrentDirectory('C:\\totallynormalfolder');
          response = 'Directory changed to C:\\totallynormalfolder';
        } else {
          response = 'Invalid directory';
        }
        break;

      case 'cd ..':
        if (currentDirectory === 'C:\\totallynormalfolder' || 
            currentDirectory === 'C:\\davidos' || 
            currentDirectory === 'C:\\Users') {
          setCurrentDirectory('C:\\');
          response = 'Directory changed to C:\\';
        } else if (currentDirectory === 'C:\\Users\\Guest') {
          setCurrentDirectory('C:\\Users');
          response = 'Directory changed to C:\\Users';
        } else if (currentDirectory === 'C:\\Users\\Guest\\Documents') {
          setCurrentDirectory('C:\\Users\\Guest');
          response = 'Directory changed to C:\\Users\\Guest';
        } else {
          response = 'Already in root directory';
        }
        break;

      case 'disco.exe':
        if (currentDirectory !== 'C:\\totallynormalfolder') {
          response = 'The system cannot find the file specified.';
        } else {
          setAwaitingDiscoConfirmation(true);
          response = 'u sure u want to do this?... (y) or (n)';
        }
        break;

      case 'date':
        response = new Date().toLocaleDateString();
        break;

      case 'time':
        response = new Date().toLocaleTimeString();
        break;

      case 'important_system_file.dll':
        if (currentDirectory === 'C:\\') {
          startMiningAnimation();
          return;
        } else {
          response = 'The system cannot find the file specified.';
        }
        break;

      case 'readme.txt':
        if (currentDirectory === 'C:\\') {
          response = 'Opening readme.txt...';
          onOpenAbout();
        } else {
          response = 'The system cannot find the file specified.';
        }
        break;

      case 'snake.txt':
        if (currentDirectory === 'C:\\') {
          response = `
if u want to play snake

just write it down...`;
        } else {
          response = 'The system cannot find the file specified.';
        }
        break;

      case 'davidos.txt':
        if (currentDirectory === 'C:\\Users\\Guest\\Documents') {
          onOpenAbout(); // This opens DavidOSInfo.jsx
          response = 'Opening davidos.txt...';
        } else {
          response = 'The system cannot find the file specified.';
        }
        break;

      case 'apple farm.txt':
        if (currentDirectory === 'C:\\Users\\Guest\\Documents') {
          onOpenAppleFarm && onOpenAppleFarm(); // This opens AppleFarmInfo.jsx
          response = 'Opening apple farm.txt...';
        } else {
          response = 'The system cannot find the file specified.';
        }
        break;

      default:
        if (command.startsWith('cd ')) {
          response = `The system cannot find the path specified.
* or maybe it exists and I just didn't code it ¯\\_(ツ)_/¯ *`;
        } else if (command.startsWith('type ')) {
          response = 'The system cannot find the file specified.';
        } else {
          response = `'${command}' is not recognized as an internal or external command.
* probably should have added more commands... maybe next time *`;
        }
    }

    setHistory(prev => [...prev, `> ${command}`, response, '']);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleCommand(input);
      setInput('');
    }
  };

  // Add cleanup in your component
  useEffect(() => {
    return () => {
      if (miningInterval.current) {
        clearInterval(miningInterval.current);
      }
    };
  }, []);

  return (
    <Window
      className={`cmd-window ${isMelting ? 'melting' : ''}`}
      title="Command Prompt"
      onClose={onClose}
      isActive={isActive}
      onActivate={onActivate}
      isMinimized={isMinimized}
      isMaximized={isMaximized}
      onMinimize={onMinimize}
      onMaximize={onMaximize}
      initialPosition={initialPosition}
      defaultWidth="640px"
      defaultHeight="480px"
    >
      <Terminal ref={terminalRef} className={isMelting ? 'melting' : ''}>
        {history.map((line, i) => (
          <div key={i}>
            {line.startsWith('>') ? (
              <CommandLine>
                <Prompt>{currentDirectory}&gt;</Prompt>
                {line.slice(2)}
              </CommandLine>
            ) : (
              line
            )}
          </div>
        ))}
        <CommandLine>
          <Prompt>{currentDirectory}&gt;</Prompt>
          <Input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        </CommandLine>
      </Terminal>
      
      {isDisco && (
        <>
          <DiscoOverlay />
          <DiscoBall />
          <Confetti ref={confettiRef} />
        </>
      )}
    </Window>
  );
};

// Add this to your global styles
const globalStyles = `
  @keyframes fall {
    to {
      transform: translateY(100vh) rotate(960deg);
    }
  }
`; 